// Labels Manager Modal:
// Create, edit, and delete tenant labels with color selection,
// cascading deletion warning, and legacy labels migration trigger.

import { useCallback, useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { labelSave, labelDelete, migrateLabels, type LabelDoc } from '../../lib/crm';

export interface LabelsManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  labels: LabelDoc[];
}

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#64748B', // Slate
];

export default function LabelsManagerModal({
  isOpen,
  onClose,
  labels,
}: LabelsManagerModalProps) {
  const { t } = useI18n();

  // Create / Edit state
  const [editingLabel, setEditingLabel] = useState<LabelDoc | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Delete confirm state
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Migration state
  const [migrating, setMigrating] = useState(false);
  const [migrateMsg, setMigrateMsg] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(isOpen, panelRef, onClose);

  const confirmRef = useRef<HTMLDivElement>(null);
  const confirmTitleId = useId();
  const closeConfirm = useCallback(() => setDeleteId(null), []);
  useDialogA11y(isOpen && deleteId !== null, confirmRef, closeConfirm);

  if (!isOpen) return null;

  function startEdit(l: LabelDoc) {
    setEditingLabel(l);
    setName(l.name);
    setColor(l.color || '#3B82F6');
    setError(null);
  }

  function resetForm() {
    setEditingLabel(null);
    setName('');
    setColor('#3B82F6');
    setError(null);
  }

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await labelSave({
        id: editingLabel ? editingLabel.id : undefined,
        name: trimmed,
        color,
      });
      resetForm();
    } catch (err) {
      setError((err as Error).message || 'Failed to save label');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    setBusy(true);
    setError(null);
    try {
      await labelDelete({ id });
      setDeleteId(null);
      if (editingLabel?.id === id) resetForm();
    } catch (err) {
      setError((err as Error).message || 'Failed to delete label');
    } finally {
      setBusy(false);
    }
  }

  async function handleMigrate() {
    setMigrating(true);
    setMigrateMsg(null);
    try {
      const res = await migrateLabels({});
      setMigrateMsg(
        `Migration complete: ${res.migratedCount} subscribers migrated, ${res.createdLabelsCount} labels created.`,
      );
    } catch (err) {
      setError((err as Error).message || 'Failed to migrate legacy labels');
    } finally {
      setMigrating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 id={titleId} className="text-base font-bold text-slate-900">
              {t('subs.labels.title') || 'Manage Labels'}
            </h3>
            <p className="text-xs text-slate-500">
              {t('subs.labels.subtitle') || 'Organize and segment subscribers with colored tags'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* Create / Edit Form */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {editingLabel
                ? t('subs.labels.edit') || 'Edit Label'
                : t('subs.labels.create') || 'Create New Label'}
            </h4>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {t('subs.labels.name') || 'Label Name'}
                </label>
                <input
                  type="text"
                  value={name}
                  maxLength={64}
                  placeholder={t('subs.labelNamePlaceholder') || 'e.g. VIP Customer'}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  {t('subs.labels.color') || 'Label Color'}
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`h-6 w-6 rounded-full transition-transform ${
                        color.toUpperCase() === c.toUpperCase()
                          ? 'scale-125 ring-2 ring-indigo-500 ring-offset-2'
                          : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                  <input
                    type="text"
                    value={color}
                    maxLength={7}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-20 rounded-md border border-slate-200 bg-white px-2 py-1 font-mono text-[11px] outline-none"
                  />
                </div>
              </div>

              {error && <p className="text-xs font-medium text-red-600">⚠️ {error}</p>}

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy || !name.trim()}
                  className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Saving...' : editingLabel ? 'Update Label' : 'Save Label'}
                </button>
                {editingLabel && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Existing Labels List */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {t('subs.labels.list') || 'Existing Labels'} ({labels.length})
            </h4>

            {labels.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">
                No labels created yet.
              </p>
            ) : (
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                {labels.map((l) => (
                  <div
                    key={l.id}
                    className="flex items-center justify-between p-3 text-xs hover:bg-slate-50/80"
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className="h-3 w-3 rounded-full bg-info"
                        style={l.color ? { backgroundColor: l.color } : undefined}
                      />
                      <span className="font-semibold text-slate-800">{l.name}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(l)}
                        className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteId(l.id)}
                        className="rounded px-2 py-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Legacy Labels Migration */}
          <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-700">Migrate Legacy Labels</p>
              <p className="text-[11px] text-slate-500">
                Sync any string labels stored on subscribers into structured label documents.
              </p>
            </div>
            <button
              type="button"
              onClick={handleMigrate}
              disabled={migrating}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {migrating ? 'Migrating...' : 'Run Migration'}
            </button>
          </div>

          {migrateMsg && (
            <p className="text-xs font-medium text-emerald-600">✓ {migrateMsg}</p>
          )}
        </div>

        {/* Delete Confirmation Modal Overlay */}
        {deleteId && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-slate-900/40 p-4">
            <div
              ref={confirmRef}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby={confirmTitleId}
              className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl space-y-3"
            >
              <h4 id={confirmTitleId} className="text-sm font-bold text-slate-900">Delete Label?</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Deleting this label will automatically remove it from all matching subscribers. This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setDeleteId(null)}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(deleteId)}
                  disabled={busy}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700"
                >
                  {busy ? 'Deleting...' : 'Delete Label'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
